import React, { useState } from 'react';
import { XIcon, CheckIcon, PlayIcon, MailIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
interface RunPlayWizardProps {
  isOpen: boolean;
  onClose: () => void;
}
export function RunPlayWizard({
  isOpen,
  onClose
}: RunPlayWizardProps) {
  const [step, setStep] = useState(1);
  const [selectedPlay, setSelectedPlay] = useState('');
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['email']);
  const [timing, setTiming] = useState('immediate');
  if (!isOpen) return null;
  const plays = [{
    id: 'high-intent-blitz',
    name: 'High-Intent Account Blitz',
    touchpoints: 8,
    duration: '14 days'
  }, {
    id: 'executive-nurture',
    name: 'Executive Nurture',
    touchpoints: 12,
    duration: '21 days'
  }, {
    id: 'plg-activation',
    name: 'PLG Activation',
    touchpoints: 6,
    duration: '7 days'
  }];
  const channels = [{
    id: 'email',
    name: 'Email',
    icon: MailIcon
  }, {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: MessageSquareIcon
  }, {
    id: 'call',
    name: 'Phone Call',
    icon: PhoneIcon
  }];
  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };
  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };
  const handleLaunch = () => {
    // Handle play launch
    onClose();
  };
  return <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} data-id="element-3031" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3032">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl" data-id="element-3033">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-3034">
            <div data-id="element-3035">
              <h2 className="text-xl font-semibold text-gray-900" data-id="element-3036">
                Launch GTM Play
              </h2>
              <p className="text-sm text-gray-600" data-id="element-3037">
                Step {step} of 3: Configure your campaign
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-3038">
              <XIcon className="w-5 h-5 text-gray-500" data-id="element-3039" />
            </button>
          </div>
          {/* Progress Steps */}
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-3040">
            <div className="flex items-center justify-between" data-id="element-3041">
              {[1, 2, 3].map(s => <div key={s} className="flex items-center flex-1" data-id="element-3042">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${s < step ? 'bg-green-600 text-white' : s === step ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-3043">
                    {s < step ? <CheckIcon className="w-5 h-5" data-id="element-3044" /> : s}
                  </div>
                  {s < 3 && <div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-green-600' : 'bg-gray-200'}`} data-id="element-3045" />}
                </div>)}
            </div>
          </div>
          {/* Content */}
          <div className="p-6" data-id="element-3046">
            {/* Step 1: Select Play */}
            {step === 1 && <div className="space-y-4" data-id="element-3047">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3048">
                  Select a GTM Play
                </h3>
                <div className="space-y-3" data-id="element-3049">
                  {plays.map(play => <button key={play.id} onClick={() => setSelectedPlay(play.id)} className={`w-full p-4 border-2 rounded-lg text-left transition-all ${selectedPlay === play.id ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3050">
                      <div className="flex items-center justify-between" data-id="element-3051">
                        <div data-id="element-3052">
                          <p className="font-semibold text-gray-900" data-id="element-3053">
                            {play.name}
                          </p>
                          <p className="text-sm text-gray-600 mt-1" data-id="element-3054">
                            {play.touchpoints} touchpoints • {play.duration}
                          </p>
                        </div>
                        {selectedPlay === play.id && <CheckIcon className="w-6 h-6 text-red-600" data-id="element-3055" />}
                      </div>
                    </button>)}
                </div>
              </div>}
            {/* Step 2: Configure Channels */}
            {step === 2 && <div className="space-y-4" data-id="element-3056">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3057">
                  Select Channels
                </h3>
                <div className="grid grid-cols-3 gap-4" data-id="element-3058">
                  {channels.map(channel => {
                const Icon = channel.icon;
                return <button key={channel.id} onClick={() => {
                  setSelectedChannels(prev => prev.includes(channel.id) ? prev.filter(c => c !== channel.id) : [...prev, channel.id]);
                }} className={`p-6 border-2 rounded-lg text-center transition-all ${selectedChannels.includes(channel.id) ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3059">
                        <Icon className={`w-8 h-8 mx-auto mb-2 ${selectedChannels.includes(channel.id) ? 'text-red-600' : 'text-gray-400'}`} data-id="element-3060" />
                        <p className="font-medium text-gray-900" data-id="element-3061">
                          {channel.name}
                        </p>
                      </button>;
              })}
                </div>
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-3062">
                  <p className="text-sm text-blue-900 font-medium mb-1" data-id="element-3063">
                    Multi-Channel Strategy
                  </p>
                  <p className="text-sm text-blue-700" data-id="element-3064">
                    Using multiple channels increases engagement by 3-4x
                    compared to single-channel campaigns
                  </p>
                </div>
              </div>}
            {/* Step 3: Schedule & Review */}
            {step === 3 && <div className="space-y-6" data-id="element-3065">
                <div data-id="element-3066">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3067">
                    Schedule Launch
                  </h3>
                  <div className="space-y-3" data-id="element-3068">
                    <button onClick={() => setTiming('immediate')} className={`w-full p-4 border-2 rounded-lg text-left transition-all ${timing === 'immediate' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3069">
                      <p className="font-semibold text-gray-900" data-id="element-3070">
                        Launch Immediately
                      </p>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-3071">
                        Start sending within 5 minutes
                      </p>
                    </button>
                    <button onClick={() => setTiming('scheduled')} className={`w-full p-4 border-2 rounded-lg text-left transition-all ${timing === 'scheduled' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3072">
                      <p className="font-semibold text-gray-900" data-id="element-3073">
                        Schedule for Later
                      </p>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-3074">
                        Choose a specific date and time
                      </p>
                    </button>
                  </div>
                </div>
                {timing === 'scheduled' && <div className="grid grid-cols-2 gap-4" data-id="element-3075">
                    <div data-id="element-3076">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-3077">
                        Start Date
                      </label>
                      <input type="date" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-3078" />
                    </div>
                    <div data-id="element-3079">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-3080">
                        Start Time
                      </label>
                      <input type="time" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-3081" />
                    </div>
                  </div>}
                {/* Summary */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-3082">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-3083">
                    Campaign Summary
                  </h4>
                  <div className="space-y-2 text-sm" data-id="element-3084">
                    <div className="flex justify-between" data-id="element-3085">
                      <span className="text-gray-600" data-id="element-3086">Play:</span>
                      <span className="font-medium text-gray-900" data-id="element-3087">
                        {plays.find(p => p.id === selectedPlay)?.name}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-3088">
                      <span className="text-gray-600" data-id="element-3089">Channels:</span>
                      <span className="font-medium text-gray-900" data-id="element-3090">
                        {selectedChannels.length} selected
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-3091">
                      <span className="text-gray-600" data-id="element-3092">Target Accounts:</span>
                      <span className="font-medium text-gray-900" data-id="element-3093">
                        1,247 accounts
                      </span>
                    </div>
                  </div>
                </div>
              </div>}
          </div>
          {/* Actions */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-3094">
            <button onClick={handleBack} disabled={step === 1} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3095">
              Back
            </button>
            <div className="flex gap-3" data-id="element-3096">
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-3097">
                Cancel
              </button>
              {step < 3 ? <button onClick={handleNext} disabled={step === 1 && !selectedPlay || step === 2 && selectedChannels.length === 0} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3098">
                  Next
                </button> : <button onClick={handleLaunch} className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium" data-id="element-3099">
                  <PlayIcon className="w-4 h-4" data-id="element-3100" />
                  Launch Play
                </button>}
            </div>
          </div>
        </div>
      </div>
    </>;
}